/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './subjectnotes.css';

const Icons = {
    Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Star: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 16.5 13.5 18.8 21 12 16.8 5.2 21 7.5 13.5 2 9 9 9 12 2"></polygon></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Trash: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Calendar: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    Clock: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
    Filter: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>,
    Link: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>,
    Megaphone: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11v2"></path><path d="M6 10v4"></path><path d="M9 9v6"></path><path d="M12 8v8"></path><path d="M15 7v10"></path><path d="M18 6v12"></path><path d="M21 5v14"></path></svg>,
    Note: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"></path><path d="M8 8h8"></path><path d="M8 12h8"></path><path d="M8 16h5"></path></svg>
};

export default function Notes() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notes, setNotes] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterImportant, setFilterImportant] = useState(false);
    const [sortBy, setSortBy] = useState('created_at');
    const [error, setError] = useState('');
    const [canCreateSchoolNotes, setCanCreateSchoolNotes] = useState(false);
    const [composeScope, setComposeScope] = useState('subject');
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    const [noteText, setNoteText] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [isImportant, setIsImportant] = useState(false);

    const emitNotesUpdated = (detail = {}) => {
        try {
            window.dispatchEvent(new CustomEvent('notesUpdated', { detail }));
        } catch {}
    };

    const loadNotes = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_all_notes.php`, {
                params: { sort_by: 'created_at', sort_order: 'DESC' }
            });

            if (!response.data.success) {
                setError(response.data.error || 'Failed to load notes');
                setNotes([]);
                setSubjects([]);
                setCanCreateSchoolNotes(false);
                return;
            }

            const fetchedSubjects = Array.isArray(response.data.subjects) ? response.data.subjects : [];
            setNotes(response.data.notes || []);
            setSubjects(fetchedSubjects);
            setCanCreateSchoolNotes(Boolean(response.data.permissions?.can_create_school_notes));
        } catch {
            setError('Failed to load notes');
            setNotes([]);
            setSubjects([]);
            setCanCreateSchoolNotes(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        if (subjects.length > 0 && !selectedSubjectId) {
            setSelectedSubjectId(String(subjects[0].id));
        }

        if (subjects.length === 0 && canCreateSchoolNotes && composeScope === 'subject') {
            setComposeScope('school');
        }
    }, [subjects, selectedSubjectId, canCreateSchoolNotes, composeScope]);

    useEffect(() => {
        const onNotesUpdated = () => {
            loadNotes();
        };

        window.addEventListener('notesUpdated', onNotesUpdated);
        return () => window.removeEventListener('notesUpdated', onNotesUpdated);
    }, [loadNotes]);

    const filteredNotes = useMemo(() => {
        let filtered = [...notes];

        if (filterStatus !== 'all') {
            if (filterStatus === 'done') {
                filtered = filtered.filter((note) => note.is_done === 1);
            } else {
                filtered = filtered.filter((note) => note.status === filterStatus && note.is_done === 0);
            }
        }

        if (filterImportant) {
            filtered = filtered.filter((note) => note.is_important === 1);
        }

        filtered.sort((left, right) => {
            switch (sortBy) {
                case 'due_date':
                    if (!left.due_date && !right.due_date) return 0;
                    if (!left.due_date) return 1;
                    if (!right.due_date) return -1;
                    return new Date(left.due_date) - new Date(right.due_date);
                case 'subject_name':
                    return (left.subject_name || '').localeCompare(right.subject_name || '');
                default:
                    return new Date(right.created_at) - new Date(left.created_at);
            }
        });

        return filtered;
    }, [notes, filterStatus, filterImportant, sortBy]);

    const getDueDateStatus = (note) => {
        if (!note?.due_date) return { status: 'none', label: '' };

        const now = new Date();
        const due = new Date(note.due_date);
        const diffMs = due - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (note.is_done === 1) {
            return { status: 'done', label: 'Completed' };
        }
        if (diffMs < 0) {
            return { status: 'overdue', label: 'Overdue' };
        }
        if (diffDays === 0) {
            return { status: 'due-today', label: 'Due Today' };
        }
        if (diffDays <= 3) {
            return { status: 'due-soon', label: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}` };
        }
        if (diffDays <= 7) {
            return { status: 'due-week', label: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}` };
        }
        return { status: 'future', label: `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}` };
    };

    const formatSubjectOption = (subject) => {
        const parts = [subject.name];
        if (subject.grade !== undefined && subject.grade !== null && subject.grade !== '') {
            parts.push(`Grade ${subject.grade}`);
        }
        if (subject.class_name) {
            parts.push(subject.class_name);
        }
        return parts.join(' | ');
    };

    const getNoteContext = (note) => {
        if (note.note_scope === 'school') {
            return note.creator_name ? `School-wide · Added by ${note.creator_name}` : 'School-wide';
        }

        const parts = [note.subject_name];
        if (note.subject_grade !== undefined && note.subject_grade !== null && note.subject_grade !== '') {
            parts.push(`Grade ${note.subject_grade}`);
        }
        if (note.class_name) {
            parts.push(note.class_name);
        }
        return parts.filter(Boolean).join(' | ');
    };

    const handleCreateNote = async (event) => {
        event.preventDefault();

        const cleanText = noteText.trim();
        if (!cleanText) return;

        if (composeScope === 'subject' && !selectedSubjectId) {
            alert('Choose a subject first.');
            return;
        }

        if (composeScope === 'school' && !canCreateSchoolNotes) {
            alert('Only admins can create school-wide notes.');
            return;
        }

        setSaving(true);
        try {
            const endpoint = composeScope === 'school' ? 'school_notes.php' : 'subject_notes.php';
            const payload = {
                action: 'create',
                note_text: cleanText,
                is_important: isImportant ? 1 : 0,
                due_date: dueDate || null
            };

            if (composeScope === 'subject') {
                payload.subject_id = selectedSubjectId;
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, payload);
            if (!response.data.success) {
                alert(response.data.error || 'Failed to save note');
                return;
            }

            setNoteText('');
            setDueDate('');
            setIsImportant(false);
            await loadNotes();
            emitNotesUpdated(composeScope === 'subject' ? { subject_id: selectedSubjectId } : { scope: 'school' });
        } catch (requestError) {
            alert(requestError.response?.data?.error || 'Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const updateNote = async (note, patch) => {
        const endpoint = note.note_scope === 'school' ? 'school_notes.php' : 'subject_notes.php';

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                action: 'update',
                note_id: note.id,
                ...patch
            });

            if (!response.data.success) {
                alert(response.data.error || 'Failed to update note');
                return;
            }

            await loadNotes();
            emitNotesUpdated(note.note_scope === 'subject' ? { subject_id: note.subject_id } : { scope: 'school' });
        } catch (requestError) {
            alert(requestError.response?.data?.error || 'Failed to update note');
        }
    };

    const deleteNote = async (note) => {
        if (!window.confirm('Delete this note?')) return;

        const endpoint = note.note_scope === 'school' ? 'school_notes.php' : 'subject_notes.php';

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                action: 'delete',
                note_id: note.id
            });

            if (!response.data.success) {
                alert(response.data.error || 'Failed to delete note');
                return;
            }

            await loadNotes();
            emitNotesUpdated(note.note_scope === 'subject' ? { subject_id: note.subject_id } : { scope: 'school' });
        } catch (requestError) {
            alert(requestError.response?.data?.error || 'Failed to delete note');
        }
    };

    if (loading) {
        return <div className="loading_state">Loading notes...</div>;
    }

    const canSaveSubjectNote = composeScope === 'subject' && selectedSubjectId;
    const canSaveSchoolNote = composeScope === 'school' && canCreateSchoolNotes;
    const canSubmit = noteText.trim() !== '' && !saving && (canSaveSubjectNote || canSaveSchoolNote);

    return (
        <div className="subject-notes-page">
            <div className="notes-header">
                <div>
                    <p className="notes-kicker">Notes & reminders</p>
                    <h1>All Notes</h1>
                    <p className="notes-subtitle">
                        Add reminders for your own subjects here. Admins can also post school-wide meetings, deadlines, and notices for everyone.
                    </p>
                </div>
            </div>

            {error && (
                <div className="notes-card notes-error-card">
                    {error}
                </div>
            )}

            <form className="notes-card notes-composer" onSubmit={handleCreateNote}>
                <div className="notes-meta">
                    <span>{notes.length} notes</span>
                    <span>{subjects.length} subject{subjects.length === 1 ? '' : 's'}</span>
                    {canCreateSchoolNotes ? <span>School-wide enabled</span> : null}
                </div>

                {canCreateSchoolNotes && (
                    <div className="notes-scope-row">
                        <button
                            type="button"
                            className={`scope-toggle-btn ${composeScope === 'subject' ? 'active' : ''}`}
                            onClick={() => setComposeScope('subject')}
                        >
                            <Icons.Link /> Subject note
                        </button>
                        <button
                            type="button"
                            className={`scope-toggle-btn ${composeScope === 'school' ? 'active' : ''}`}
                            onClick={() => setComposeScope('school')}
                        >
                            <Icons.Megaphone /> School-wide note
                        </button>
                    </div>
                )}

                {composeScope === 'subject' ? (
                    <div className="notes-field-group">
                        <label className="notes-label" htmlFor="note-subject-select">Subject</label>
                        <select
                            id="note-subject-select"
                            className="notes-select"
                            value={selectedSubjectId}
                            onChange={(event) => setSelectedSubjectId(event.target.value)}
                            disabled={subjects.length === 0}
                        >
                            {subjects.length === 0 && <option value="">No subjects available</option>}
                            {subjects.map((subject) => (
                                <option key={subject.id} value={subject.id}>
                                    {formatSubjectOption(subject)}
                                </option>
                            ))}
                        </select>
                        {subjects.length === 0 && (
                            <p className="notes-helper-banner muted">
                                No personal subjects are available yet. Add a subject first, or switch to a school-wide note if you are an admin.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="notes-helper-banner">
                        This note will be visible to everyone in your school and can be used for meetings, deadlines, and shared reminders.
                    </div>
                )}

                <label className="notes-label" htmlFor="notes-main-textarea">Add a note</label>
                <textarea
                    id="notes-main-textarea"
                    className="notes-textarea"
                    value={noteText}
                    onChange={(event) => setNoteText(event.target.value)}
                    placeholder={composeScope === 'school'
                        ? 'Example: Staff meeting on Thursday at 14:00, moderation deadline on Friday...'
                        : 'Example: Test on Friday, bring calculators, parent meeting next week...'}
                    rows={5}
                />

                <div className="notes-date-row">
                    <label htmlFor="notes-due-date" className="note-date-label">
                        <Icons.Calendar /> Due Date (optional)
                    </label>
                    <input
                        id="notes-due-date"
                        type="datetime-local"
                        className="note-date-input"
                        value={dueDate}
                        onChange={(event) => setDueDate(event.target.value)}
                    />
                </div>

                <div className="notes-compose-actions">
                    <label className="important-toggle">
                        <input type="checkbox" checked={isImportant} onChange={(event) => setIsImportant(event.target.checked)} />
                        <span>Important</span>
                    </label>
                    <button className="notes-save-btn" type="submit" disabled={!canSubmit}>
                        {saving ? 'Saving...' : <><Icons.Plus /> Add note</>}
                    </button>
                </div>
            </form>

            <div className="notes-card notes-filter-card">
                <div className="notes-filter-grid">
                    <div className="notes-field-group">
                        <label className="notes-label" htmlFor="notes-filter-status">
                            <Icons.Filter /> Status
                        </label>
                        <select
                            id="notes-filter-status"
                            className="notes-select"
                            value={filterStatus}
                            onChange={(event) => setFilterStatus(event.target.value)}
                        >
                            <option value="all">All Notes</option>
                            <option value="overdue">Overdue</option>
                            <option value="approaching">Approaching</option>
                            <option value="future">Future</option>
                            <option value="done">Completed</option>
                        </select>
                    </div>

                    <div className="notes-field-group">
                        <label className="notes-label" htmlFor="notes-filter-sort">
                            <Icons.Star /> Sort By
                        </label>
                        <select
                            id="notes-filter-sort"
                            className="notes-select"
                            value={sortBy}
                            onChange={(event) => setSortBy(event.target.value)}
                        >
                            <option value="created_at">Recently Created</option>
                            <option value="due_date">Due Date</option>
                            <option value="subject_name">Subject / Scope</option>
                        </select>
                    </div>

                    <label className="notes-checkbox-row">
                        <input
                            type="checkbox"
                            checked={filterImportant}
                            onChange={(event) => setFilterImportant(event.target.checked)}
                        />
                        <span>Important only</span>
                    </label>
                </div>
            </div>

            {filteredNotes.length > 0 ? (
                <div className="notes-list">
                    {filteredNotes.map((note) => {
                        const dueStatus = getDueDateStatus(note);
                        return (
                            <article
                                key={`${note.note_scope}-${note.id}`}
                                className={`note-card ${note.is_important ? 'important' : ''} ${note.is_done ? 'done' : ''} ${note.due_date ? `due-${dueStatus.status}` : ''}`}
                            >
                                <div className="note-card-top">
                                    <div className="note-card-meta">
                                        <span className="note-date">{new Date(note.created_at).toLocaleString()}</span>
                                        <span className={`note-pill ${note.note_scope === 'school' ? 'school-pill' : 'subject-pill'}`}>
                                            {note.note_scope === 'school' ? <Icons.Megaphone /> : <Icons.Link />}
                                            {note.scope_label}
                                        </span>
                                        {note.is_important ? <span className="note-pill important-pill"><Icons.Star /> Important</span> : null}
                                        {note.is_done ? <span className="note-pill done-pill"><Icons.Check /> Done</span> : null}
                                        {note.due_date && dueStatus.status !== 'none' && dueStatus.status !== 'done' ? (
                                            <span className={`note-pill due-pill due-${dueStatus.status}`}>
                                                <Icons.Clock /> {dueStatus.label}
                                            </span>
                                        ) : null}
                                    </div>

                                    {(note.can_edit || note.can_delete || note.can_toggle_done) && (
                                        <div className="note-card-actions">
                                            {note.can_edit ? (
                                                <button
                                                    type="button"
                                                    className={`note-action-btn ${note.is_important ? 'active' : ''}`}
                                                    onClick={() => updateNote(note, { is_important: note.is_important ? 0 : 1 })}
                                                    title="Mark important"
                                                >
                                                    <Icons.Star />
                                                </button>
                                            ) : null}
                                            {note.can_toggle_done ? (
                                                <button
                                                    type="button"
                                                    className={`note-action-btn ${note.is_done ? 'active' : ''}`}
                                                    onClick={() => updateNote(note, { is_done: note.is_done ? 0 : 1 })}
                                                    title="Mark done"
                                                >
                                                    <Icons.Check />
                                                </button>
                                            ) : null}
                                            {note.can_delete ? (
                                                <button
                                                    type="button"
                                                    className="note-action-btn danger"
                                                    onClick={() => deleteNote(note)}
                                                    title="Delete note"
                                                >
                                                    <Icons.Trash />
                                                </button>
                                            ) : null}
                                        </div>
                                    )}
                                </div>

                                <p className="note-text">{note.note_text}</p>
                                <div className="note-context-line">{getNoteContext(note)}</div>
                            </article>
                        );
                    })}
                </div>
            ) : (
                <div className="notes-empty-state">
                    <Icons.Note />
                    <h3>No notes found</h3>
                    <p>Try adjusting your filters, or add a new subject note or school-wide reminder above.</p>
                </div>
            )}
        </div>
    );
}
